import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { EM_DASH } from '~/common/constants/unicode';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { useDeleteModal } from '~/eds/src/components/hooks/useDeleteModal';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Tabs } from '~/eds/src/components/Tabs/Tabs';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { Text } from '~/eds/src/components/Text/Text';
import { Toggle } from '~/eds/src/components/Toggle/Toggle';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { Table } from '~/eds/src/modules/Table/Table';
import { parseDate } from '~/eds/src/utils/parseDate';
import { AGENT_IDS } from '~/features/ask-anything/chat-bot-panel/agentIds';
import { COLLAPSED_WIDTH } from '~/features/ask-anything/chat-bot-panel/constants';
import { usePermissions } from '~/hooks/usePermissions';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { PaginationParams } from '~/types/Pagination';
import { ERROR, SUCCESS } from '~/types/toast.types';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';

import { GeneralRoutingTab } from './GeneralRoutingTab';
import { RoutingTablePanel } from './RoutingTablePanel';
import { ActivationStatus, Route } from './types';

const ROUTING_TABLE_PERMISSIONS = [
  { resourceId: 'workflow_templates', resourceType: 'view' },
  { resourceId: 'conversational_ai', resourceType: 'view' },
];

enum RenderState {
  LOADING = 'loading',
  READY = 'ready',
  ERROR = 'error',
  NO_DATA = 'nodata',
}

type RoutingTableProps = {
  showCreatePanel?: boolean;
  onCreatePanelClose?: () => void;
};

export const RoutingTable = ({
  showCreatePanel = false,
  onCreatePanelClose,
}: RoutingTableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { checkPermission } = usePermissions({
    permissions: ROUTING_TABLE_PERMISSIONS,
  });

  const canManageRouting = Boolean(
    checkPermission('workflow_templates', 'view'),
  );
  const canSeeTestRoutingButton =
    canManageRouting && Boolean(checkPermission('conversational_ai', 'view'));

  const [currentTab, setCurrentTab] = useState('specific-routing');
  const [debouncedSearchString, searchString, setSearchString] =
    useDebouncedState('');
  const [pagination, setPagination] = useState<PaginationParams>({
    pageIndex: 1,
    pageSize: 10,
  });

  const [isEditPanelVisible, setIsEditPanelVisible] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [routeToDelete, setRouteToDelete] = useState<Route | null>(null);

  const [localToggleState, setLocalToggleState] = useState<boolean>(false);

  const { data: routingConfig, refetch: refetchConfig } =
    api.endpoints.getRoutingConfiguration.useQuery(undefined);

  const [patchRoutingConfiguration, { isLoading: isTogglingActivation }] =
    api.endpoints.patchRoutingConfiguration.useMutation();

  useEffect(() => {
    if (routingConfig) {
      setLocalToggleState(routingConfig.allUserActivationStatus === 'active');
    }
  }, [routingConfig]);

  useEffect(() => {
    if (showCreatePanel) {
      setSelectedRoute(null);
    }
  }, [showCreatePanel]);

  const isPanelVisible = showCreatePanel || isEditPanelVisible;

  const { data, isFetching, error, refetch } =
    api.endpoints.searchRouting.useQuery({
      pageSize: pagination.pageSize,
      currentPage: pagination.pageIndex,
      searchString: debouncedSearchString || undefined,
    });

  const [deleteRouting, { isLoading: isDeleting }] =
    api.endpoints.deleteRouting.useMutation();

  const [deleteModal, showDeleteModal, hideDeleteModal] = useDeleteModal({
    title: intl.formatMessage(
      {
        id: 'RoutingTable.deleteModalTitle',
        defaultMessage: 'Delete "{routeName}"?',
        description: 'Title for delete route confirmation modal',
      },
      { routeName: routeToDelete?.routeName || '' },
    ),
    children: (
      <Text>
        {intl.formatMessage(
          {
            id: 'RoutingTable.deleteModalMessage',
            defaultMessage:
              'Are you sure you want to delete "{routeName}"? This action cannot be undone.',
            description: 'Message for delete route confirmation modal',
          },
          { routeName: routeToDelete?.routeName || '' },
        )}
      </Text>
    ),
    onDelete: async () => {
      if (!routeToDelete) return;

      try {
        await deleteRouting({ routingId: routeToDelete.id }).unwrap();
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'RoutingTable.deleteSuccess',
              defaultMessage: '"{routeName}" has been deleted.',
              description: 'Success message when route is deleted',
            },
            { routeName: routeToDelete.routeName },
          ),
        );
        setRouteToDelete(null);
        hideDeleteModal();
        refetch();
      } catch {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'RoutingTable.deleteError',
            defaultMessage: 'Failed to delete route. Please try again.',
            description: 'Error message when route deletion fails',
          }),
        );
      }
    },
    isDeleting,
  });

  const handleDelete = useCallback(
    (route: Route) => {
      setRouteToDelete(route);
      showDeleteModal();
    },
    [showDeleteModal],
  );

  const handleSearch = useCallback((value: string | null) => {
    setSearchString(value || '');
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
  }, []);

  const handlePaginationUpdate = useCallback(
    (newPagination: Partial<PaginationParams>) => {
      setPagination((prev) => {
        const pageIndex = newPagination.pageIndex ?? prev.pageIndex;
        const pageSize = newPagination.pageSize ?? prev.pageSize;

        if (pageIndex === prev.pageIndex && pageSize === prev.pageSize) {
          return prev;
        }

        return { pageIndex, pageSize };
      });
    },
    [],
  );

  const handleEditRoute = useCallback((route: Route) => {
    setSelectedRoute(route);
    setIsEditPanelVisible(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: 'routeName',
        cellType: 'text',
        title: intl.formatMessage({
          id: 'RoutingTable.routeNameColumn',
          defaultMessage: 'Route Name',
          description: 'Column header for route name',
        }),
        minWidth: 'l',
        renderCell: (route: Route) => {
          const routeName = route.routeName || EM_DASH;
          if (routeName === EM_DASH) {
            return <Text variant="body">{routeName}</Text>;
          }
          return (
            <Layout
              direction="row"
              align="center"
              spacing={2}
              minW={0}
              overflow="hidden"
            >
              <Icon
                icon="workflow"
                size="s"
                color="status.active"
                tooltip={intl.formatMessage({
                  id: 'RoutingTable.workflowIconTooltip',
                  defaultMessage: 'Workflow',
                  description: 'Tooltip for workflow icon in route name column',
                })}
              />
              <Link
                onClick={() => handleEditRoute(route)}
                shouldTruncate={true}
              >
                {routeName}
              </Link>
            </Layout>
          );
        },
      },
      {
        key: 'routeInstructions',
        cellType: 'text',
        title: intl.formatMessage({
          id: 'RoutingTable.routeInstructionsColumn',
          defaultMessage: 'Route Instructions',
          description: 'Column header for route instructions',
        }),
        minWidth: 'l',
        renderCell: ({ instructions }: Route) => (
          <TruncateText variant="body">{instructions || EM_DASH}</TruncateText>
        ),
      },
      {
        key: 'routeDestination',
        cellType: 'text',
        title: intl.formatMessage({
          id: 'RoutingTable.routeDestinationColumn',
          defaultMessage: 'Route Destination',
          description: 'Column header for route destination',
        }),
        minWidth: 'l',
        mapCellProps: ({ routeName }: Route) => ({
          text: routeName || EM_DASH,
        }),
      },
      {
        key: 'createdByName',
        cellType: 'user',
        title: intl.formatMessage({
          id: 'RoutingTable.createdByColumn',
          defaultMessage: 'Created By',
          description: 'Column header for created by',
        }),
        minWidth: 'm',
        disableResizing: true,
        disableSortBy: true,
        mapCellProps: (route: Route) => {
          if (!route.createdById || !route.createdByName) {
            return {
              user: undefined,
              placeholder: route.createdByName || EM_DASH,
            };
          }

          const nameParts = route.createdByName.split(' ').filter(Boolean);
          const firstName = nameParts[0] || null;
          const lastName =
            nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

          return {
            user: {
              id: route.createdById,
              firstName,
              lastName,
              email: null,
            },
            mode: 'avatar-name',
          };
        },
      },
      {
        key: 'lastUpdated',
        title: intl.formatMessage({
          id: 'RoutingTable.lastUpdatedColumn',
          defaultMessage: 'Last Updated',
          description: 'Column header for last updated',
        }),
        minWidth: 'm',
        renderCell: ({ modifiedDate }: Route) => (
          <DateText
            date={parseDate(modifiedDate)}
            format="relative"
            enableTick
          />
        ),
      },
      {
        key: 'status',
        cellType: 'chips',
        title: intl.formatMessage({
          id: 'RoutingTable.statusColumn',
          defaultMessage: 'Status',
          description: 'Column header for status',
        }),
        minWidth: 'm',
        disableResizing: true,
        mapCellProps: ({ status }: Route) => ({
          chips: [
            {
              text:
                status === 'active'
                  ? intl.formatMessage({
                      id: 'RoutingTable.statusActive',
                      defaultMessage: 'Active',
                      description: 'Active status text',
                    })
                  : intl.formatMessage({
                      id: 'RoutingTable.statusInactive',
                      defaultMessage: 'Inactive',
                      description: 'Inactive status text',
                    }),
              status: status === 'active' ? 'success' : 'inactive',
            },
          ],
        }),
      },
    ],
    [intl, handleEditRoute],
  );

  const rowActions = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: 'RoutingTable.editAction',
          defaultMessage: 'Edit',
          description: 'Label for edit action',
        }),
        value: 'edit',
        onClick: handleEditRoute,
      },
      {
        label: intl.formatMessage({
          id: 'RoutingTable.deleteAction',
          defaultMessage: 'Delete',
          description: 'Label for delete action',
        }),
        value: 'delete',
        onClick: handleDelete,
      },
    ],
    [intl, handleDelete, handleEditRoute],
  );

  const handleToggleActivation = useCallback(
    async (enabled: boolean) => {
      if (enabled && (!data || data.totalRecords === 0)) {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'RoutingTable.toggleErrorNoRoutes',
            defaultMessage:
              'At least one route must be created before activating routing for all users.',
            description:
              'Error message when trying to activate toggle without routes',
          }),
        );
        return;
      }

      setLocalToggleState(enabled);

      try {
        const newStatus: ActivationStatus = enabled ? 'active' : 'inactive';
        await patchRoutingConfiguration({
          configInput: {
            allUserActivationStatus: newStatus,
            versionNumber: routingConfig?.versionNumber ?? 0,
          },
        }).unwrap();

        await refetchConfig();

        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'RoutingTable.toggleSuccess',
              defaultMessage: 'Routing has been {status} for all users.',
              description: 'Success message when toggle is changed',
            },
            { status: enabled ? 'activated' : 'deactivated' },
          ),
        );
      } catch {
        setLocalToggleState(!enabled);

        showToast(
          ERROR,
          intl.formatMessage({
            id: 'RoutingTable.toggleError',
            defaultMessage:
              'Failed to update routing status. Please try again.',
            description: 'Error message when toggle update fails',
          }),
        );
      }
    },
    [patchRoutingConfiguration, routingConfig, data, intl, refetchConfig],
  );

  const handleTestRouting = useCallback(() => {
    dispatch(
      slices.chatbot.actions.setInitialAgent(AGENT_IDS.WORKFLOW_ROUTING),
    );
    dispatch(slices.chatbot.actions.toggleChatbot());
    dispatch(slices.chatbot.actions.setWidth(COLLAPSED_WIDTH));
  }, [dispatch]);

  const tabs = [
    {
      label: intl.formatMessage({
        id: 'RoutingTable.specificRoutingTab',
        defaultMessage: 'Specific Routing',
        description: 'Tab label for Specific Routing',
      }),
      value: 'specific-routing',
      count: data?.totalRecords,
      panel: <></>,
    },
    ...(canManageRouting
      ? [
          {
            label: intl.formatMessage({
              id: 'RoutingTable.generalRoutingTab',
              defaultMessage: 'General Routing',
              description: 'Tab label for General Routing',
            }),
            value: 'general-routing',
            panel: <></>,
          },
        ]
      : []),
  ];

  const hasData = data && data.data && data.data.length > 0;

  const hasActiveRoute = useMemo(() => {
    return (
      data?.data?.some((route: Route) => route.status === 'active') ?? false
    );
  }, [data]);

  const computeRenderState = useCallback((): RenderState => {
    if (isFetching || isDeleting) {
      return RenderState.LOADING;
    }
    if (error) {
      return RenderState.ERROR;
    }
    if (!hasData) {
      return RenderState.NO_DATA;
    }
    return RenderState.READY;
  }, [isFetching, isDeleting, error, hasData]);

  const renderState = computeRenderState();

  return (
    <>
      <Layout direction="column" spacing={4}>
        <Layout direction="row" align="center" spacing={4}>
          <Layout flex="1">
            <Tabs
              enableContentPadding={false}
              selectedTab={currentTab}
              tabs={tabs}
              onSelectTab={setCurrentTab}
            />
          </Layout>
          {(canManageRouting || canSeeTestRoutingButton) && (
            <Layout direction="row" align="center" spacing={0}>
              {canSeeTestRoutingButton && (
                <>
                  <Button
                    icon="evisort-ai-static"
                    iconPosition="left"
                    text={intl.formatMessage({
                      id: 'RoutingTable.testRoutingButton',
                      defaultMessage: 'Test Routing',
                      description: 'Button text to test workflow routing',
                    })}
                    onClick={handleTestRouting}
                  />
                  <Layout pl={6}>
                    <Divider isHorizontal={false} height="s" />
                  </Layout>
                </>
              )}
              {canManageRouting && (
                <Layout direction="row" align="center" spacing={3} pl={3}>
                  <Text variant="body-bold">
                    {intl.formatMessage({
                      id: 'RoutingTable.activateToggleLabel',
                      defaultMessage: 'Activate Routing for All Users',
                      description: 'Label for activation toggle',
                    })}
                  </Text>
                  <Toggle
                    name="activate-routing"
                    enabled={localToggleState}
                    onToggle={handleToggleActivation}
                    disabled={isTogglingActivation || !hasActiveRoute}
                    tooltip={
                      !hasActiveRoute && !isTogglingActivation
                        ? intl.formatMessage({
                            id: 'RoutingTable.toggleDisabledTooltip',
                            defaultMessage:
                              'Available when there is +1 Specific Route that is active',
                            description:
                              'Tooltip for disabled toggle when no active routes exist',
                          })
                        : undefined
                    }
                  />
                </Layout>
              )}
            </Layout>
          )}
        </Layout>

        {currentTab === 'specific-routing' && (
          <Layout direction="column" spacing={4}>
            <SearchInput
              name="routing-search"
              placeholder={intl.formatMessage({
                id: 'RoutingTable.searchPlaceholder',
                defaultMessage: 'Search routes...',
                description: 'Placeholder text for route search input',
              })}
              value={searchString}
              onChange={handleSearch}
            />

            {renderState === RenderState.LOADING && (
              <LoadingContainer isLoading />
            )}

            {renderState === RenderState.READY && data && (
              <Table
                name="routing-table"
                columns={columns}
                data={data.data}
                rowActions={rowActions}
                totalCount={data.totalRecords}
                state={pagination}
                onUpdate={handlePaginationUpdate}
                options={{
                  enableManageColumns: true,
                  enableExportXlsx: false,
                  enableSelectRows: false,
                }}
              />
            )}

            {renderState === RenderState.NO_DATA && (
              <Layout align="center" justify="center" p={8}>
                <Layout direction="column" align="center" spacing={4}>
                  <Text variant="title">
                    {intl.formatMessage({
                      id: 'RoutingTable.emptyHeading',
                      defaultMessage: 'No routes found',
                      description: 'Empty state heading',
                    })}
                  </Text>
                  <Text variant="body">
                    {intl.formatMessage({
                      id: 'RoutingTable.emptyDescription',
                      defaultMessage: 'Create your first route to get started',
                      description: 'Empty state description',
                    })}
                  </Text>
                </Layout>
              </Layout>
            )}

            {renderState === RenderState.ERROR && (
              <Layout align="center" justify="center" p={8}>
                <Layout direction="column" align="center" spacing={4}>
                  <Text variant="title">
                    {intl.formatMessage({
                      id: 'RoutingTable.errorHeading',
                      defaultMessage: 'Something went wrong',
                      description: 'Error state heading',
                    })}
                  </Text>
                  <Text variant="body">
                    {intl.formatMessage({
                      id: 'RoutingTable.errorDescription',
                      defaultMessage:
                        'Unable to load routes. Please try again.',
                      description: 'Error state description',
                    })}
                  </Text>
                </Layout>
              </Layout>
            )}
          </Layout>
        )}

        {currentTab === 'general-routing' && canManageRouting && (
          <GeneralRoutingTab />
        )}
      </Layout>
      {isPanelVisible && (
        <RoutingTablePanel
          route={selectedRoute}
          onClose={() => {
            setIsEditPanelVisible(false);
            setSelectedRoute(null);
            onCreatePanelClose?.();
          }}
          onSuccess={() => {
            setIsEditPanelVisible(false);
            setSelectedRoute(null);
            onCreatePanelClose?.();
            refetch();
          }}
        />
      )}
      {deleteModal}
    </>
  );
};
